import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import Seo, { BREADCRUMB_JSONLD, COLLECTION_PAGE_JSONLD } from "@/components/Seo";
import { responsiveImage } from "@/lib/img";

// How many skeleton cards to reserve space for while the grid loads.
const SKELETON_COUNT = 5;

export default function Categories() {
  const [cats, setCats] = useState(null); // null = loading
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {
      // During the static prerender (scripts/prerender.js) a failed fetch keeps
      // the skeleton instead of the error box. The snapshot then still carries
      // this page's <h1>, the layout's <main>, and the nav and footer links, so
      // prerender.js can ship it instead of an empty #root. A real visitor
      // refetches on mount (createRoot, no hydration), so they see the true state.
      if (typeof window !== "undefined" && window.__PRERENDER__) return;
      setFailed(true);
    });
  }, []);
  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 md:py-20">
      <Seo
        title="Jewellery CAD Categories"
        description="Jewellery CAD for rings, pendants, bracelets, earrings and necklaces. Fixed-price quotations and production-ready STL, STEP and OBJ files."
        path="/categories"
        jsonLd={[COLLECTION_PAGE_JSONLD("/categories", "Jewellery CAD Categories — GetJewelDesigns"), BREADCRUMB_JSONLD([{ name: "Home", path: "/" }, { name: "Categories", path: "/categories" }])]}
      />
      <div className="text-xs uppercase tracking-[0.3em] text-primary mb-4">Collections</div>
      <h1 className="font-serif text-4xl md:text-6xl mb-4">Every category, every detail.</h1>
      {/* Static, so the page says what it offers even when the grid below has
          not loaded, including in a prerender taken with the API unreachable. */}
      <p className="text-muted-foreground text-lg leading-relaxed max-w-2xl mb-12">
        Jewellery CAD for rings, pendants, bracelets, earrings and necklaces. Every commission is
        quoted at one fixed price before work begins, and delivered as production-ready files: STL
        for printing and casting, with STEP and OBJ where relevant. Quotations are free.
      </p>
      {failed && (
        <div className="border hairline p-10 text-center">
          <p className="text-muted-foreground mb-6">We couldn't load the categories. Please try again.</p>
          <button onClick={() => window.location.reload()} className="border border-primary text-primary px-6 py-3 text-xs uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors duration-300">Retry</button>
        </div>
      )}
      {/*
        Skeletons rather than a "Loading…" line. The single line of text occupied
        almost no height, so when the grid arrived the page grew by its full
        height at once and pushed the footer down — measured at CLS 0.44 on
        mobile against a 0.25 "poor" threshold, ~100x every other page here.
        These cards reserve the real layout up front, so the swap is in place.
        SKELETON_COUNT tracks the seeded category count; being one or two out
        only changes how much space is reserved, never correctness.
      */}
      {!failed && cats === null && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" aria-hidden="true">
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <div key={i} className="border hairline overflow-hidden">
              <div className="aspect-[4/5] bg-muted animate-pulse" />
              <div className="p-6">
                <div className="h-3 w-32 bg-muted animate-pulse mb-3" />
                <div className="h-7 w-24 bg-muted animate-pulse mb-3" />
                <div className="h-4 w-full bg-muted animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {(cats || []).map((c, i) => (
          <Link key={c.id} to={`/categories/${c.slug}`} data-testid={`cats-item-${c.slug}`} className="group block border hairline overflow-hidden hover:border-primary/60 transition-colors duration-300">
            <div className="aspect-[4/5] overflow-hidden bg-muted">
              {/*
                The first card's image is the LCP element on this page and it was
                lazy-loaded, so the browser did not even begin fetching it until
                layout had run — measured 6.2s of pure load delay. Above the fold
                on every viewport, so it loads eagerly at high priority; the rest
                stay lazy.
              */}
              <img
                {...responsiveImage(c.cover_image, { width: 800, ratio: "4/5", sizes: "(min-width: 1024px) 25vw, (min-width: 768px) 50vw, 100vw" })}
                alt={c.name}
                loading={i === 0 ? "eager" : "lazy"}
                fetchPriority={i === 0 ? "high" : undefined}
                className="w-full h-full object-cover transition-opacity duration-500 group-hover:opacity-80"
              />
            </div>
            <div className="p-6">
              <div className="text-xs uppercase tracking-[0.25em] text-primary mb-2">Starting from {fmtMoney(c.starting_price, c.currency)}{c.starting_price_usd ? ` · ${fmtMoney(c.starting_price_usd, "USD")}` : ""}</div>
              <h2 className="font-serif text-2xl">{c.name}</h2>
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{c.short_desc}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
